import { BudgetLineType } from 'api/budgets/enums/BudgetLineType';
import { BudgetLineDto } from 'api/budgets/models/BudgetDto';
import {
    BudgetLineParameterDto,
    BudgetLineParameterElementDto,
    BudgetLineParameterMinMaxDto,
    BudgetElementParameterTextDto,
    BudgetElementParameterNumberDto,
    BudgetElementParameterQuantityDto,
    BudgetElementParameterPriceDto
} from 'api/budgets/models/BudgetLineParameterDto';
import ExpressionSystem, { ExpressionParameter } from 'common/components/expressionButton/ExpressionSystem';
import { ParameterSectionBaseModel, ParameterSectionParameterType, ParameterSectionType } from 'common/components/parametersSections/models';
import FamiliesService from 'api/families/FamiliesService';
import i18n from 'common/services/I18n';
import MathUtils from 'common/services/MathUtils';
import Utils from 'common/services/Utils';
import { CSSProperties } from 'react';
import { findLineByKey } from 'store/budget/reducer';
import store from 'store/store';
import colors from 'styles/export/colors.module.scss';
import type { CatalogParameterDto } from 'api/catalogParameters/models/CatalogParameterDto';

export const getFamilyParametersForBudgetLine = async (familyId: string): Promise<BudgetLineParameterDto[]> => {
    const params = await FamiliesService.getFamilyParameters(familyId);
    if (!params || params.length === 0) {
        return [];
    }

    const removeEmptySections = (nodes: BudgetLineParameterDto[]): BudgetLineParameterDto[] => {
        return nodes
            .map(node => ({
                ...node,
                children: removeEmptySections(node.children),
            }))
            .filter(node =>
                node.parameterType !== ParameterSectionParameterType.TEXT ||
                (node.children && node.children.length > 0)
            );
    };

    const mapParameter = (p: ParameterSectionBaseModel, i: number) => {
        return {
            id: undefined,
            parentId: p.parentKey,
            key: Utils.newGuid(),
            elementId: undefined,
            elementFamilyId: undefined,
            elementParameterId: undefined,
            elementParameterParentId: null,
            position: i,
            budgetLineParameterElementId: null,
            familyParameterId: p.id,
            familyId: familyId,
            catalogParameterId: p.catalogParameterId,
            children: [],
            parameterName: p.parameterName,
            parameterType: p.parameterType,
            parameterFamilyId: p.parameterFamilyId,
            parameterIsRequired: p.parameterIsRequired,
            visibility: p.visibility,
            type: p.type,
            title: p.title,
            unitId: p.unitId,
            tempId: p.key,
            tempParentId: p.parentKey,
            number: p.parameterType === ParameterSectionParameterType.NUMBER
                ? { number: null, numberExpression: p.formulaExpression ?? null, unitId: p.unitId }
                : undefined,
            quantity: p.parameterType === ParameterSectionParameterType.QUANTITY
                ? { quantity: null, quantityExpression: p.formulaExpression ?? null, unitId: p.unitId }
                : undefined,
            price: p.parameterType === ParameterSectionParameterType.PRICE
                ? { price: null, priceExpression: p.formulaExpression ?? null, unitId: p.unitId }
                : undefined,
            text: p.parameterType === ParameterSectionParameterType.TEXT
                ? { text: null }
                : undefined,
        };
    };

    const mappedParams = params.map(mapParameter).filter(p =>
        p.parameterType !== ParameterSectionParameterType.IMAGES &&
        p.parameterType !== ParameterSectionParameterType.NUMBER_MIN_MAX
    );

    const stripId = (id: string) => id.replace(/[^0-9A-Za-z_]/g, '');
    const knownIds = new Set<string>();
    mappedParams.forEach(param => {
        if (param.familyParameterId) {
            knownIds.add(stripId(param.familyParameterId));
        }
        if (param.catalogParameterId) {
            knownIds.add(stripId(param.catalogParameterId));
        }
    });

    const transformFamilyFormula = (expr: string | null | undefined): string | null => {
        if (!expr) return null;
        let result = expr;
        knownIds.forEach(id => {
            result = result.split('M_' + id + '_E').join('M_CURRENT_LINE__' + id + '__parameter_E');
            result = result.split('M_LISTOF__' + id + '_E').join('M_CURRENT_LINE__LISTOF__' + id + '__parameter_E');
        });
        // Transform composition (PRODUCTS) references using regex to catch all composition mentions
        result = result.replace(/M_((?!CURRENT_LINE__)[0-9A-Za-z_]+__composition__[0-9A-Za-z_]+)_E/g, 'M_CURRENT_LINE__$1_E');
        for (const fieldKey of ANCESTOR_FIELD_KEYS) {
            result = result.split('M_BUDGET_FIELD_SECTION__' + fieldKey + '_E').join('M_ANCESTOR_LINE__' + fieldKey + '_E');
            result = result.split('M_BUDGET_FIELD_ELEMENT__' + fieldKey + '_E').join('M_CURRENT_LINE__' + fieldKey + '_E');
            result = result.split('M_BUDGET_FIELD_PARENT__' + fieldKey + '_E').join('M_CURRENT_PARENT_LINE__' + fieldKey + '_E');
        }
        return result;
    };

    mappedParams.forEach(param => {
        if (param.number?.numberExpression) {
            param.number = { ...param.number, numberExpression: transformFamilyFormula(param.number.numberExpression) };
        }
        if (param.quantity?.quantityExpression) {
            param.quantity = { ...param.quantity, quantityExpression: transformFamilyFormula(param.quantity.quantityExpression) };
        }
        if (param.price?.priceExpression) {
            param.price = { ...param.price, priceExpression: transformFamilyFormula(param.price.priceExpression) };
        }
    });

    const paramMap = new Map<string, BudgetLineParameterDto>();
    const roots: BudgetLineParameterDto[] = [];

    mappedParams.forEach(param => {
        paramMap.set(param.tempId, param);
    });

    mappedParams.forEach(param => {
        if (param.tempParentId && paramMap.has(param.tempParentId)) {
            const parent = paramMap.get(param.tempParentId)!;
            parent.children.push(param);
        } else {
            roots.push(param);
        }
    });

    return removeEmptySections(roots);
};

export const BODY_CLICK_IGNORE_CLASS_NAME = 'BODY_CLICK_IGNORE';

// Cache for catalog parameters used in formula display
let _catalogParamsCache: CatalogParameterDto[] | null = null;
let _catalogParamsPromise: Promise<CatalogParameterDto[]> | null = null;

export function setCatalogParametersCache (params: CatalogParameterDto[]): void {
    _catalogParamsCache = params;
}

export function getCatalogParametersCache (): CatalogParameterDto[] | null {
    return _catalogParamsCache;
}

export function loadCatalogParametersForBudget (): void {
    if (_catalogParamsCache || _catalogParamsPromise) return;
    _catalogParamsPromise = import('api/catalogParameters/CatalogParametersService')
        .then(mod => mod.default.getCatalog())
        .then(data => { _catalogParamsCache = data; return data; });
}

export async function waitForCatalogParameters (): Promise<void> {
    if (_catalogParamsCache) return;
    if (_catalogParamsPromise) {
        await _catalogParamsPromise;
    }
}

// Pre-load catalog parameters eagerly
loadCatalogParametersForBudget();

const levelMultiplier = 10;
const defaultBorderRadius = 8;

export const lineLevelStyle = (level: number, clone: boolean, insideLevel: number): CSSProperties => {
    return {
        marginLeft: clone ? level : ((level - insideLevel) * levelMultiplier),
    };
}

export const lineInnerLevelStyle = (parentIsSelected: boolean, insideLevel: number): CSSProperties => {
    if (parentIsSelected) {
        return {
            paddingLeft: (insideLevel * levelMultiplier),
        };
    }
    return {};
}

export const lineOutterLineStyle = (isActive: boolean): CSSProperties => {
    return {
        marginTop: isActive ? 4 : undefined,
    };
}

export const lineActiveStyle = (isActive: boolean): CSSProperties => {
    return {
        borderWidth: isActive ? 0 : undefined,
    };
}

export const lineInnerActiveStyle = (isActive: boolean, parentIsSelected: boolean, isLast: boolean, hasChildren: boolean, isOpen: boolean, borderTopLeftWithoutRadius: boolean = true): CSSProperties => {
    const defaultBorderColor = isActive || parentIsSelected ? colors.primaryDark : 'transparent';

    let result: CSSProperties = {
        borderWidth: isActive || parentIsSelected ? 1 : 0,
        borderStyle: 'solid',
        borderTopColor: defaultBorderColor,
        borderBottomColor: defaultBorderColor,
        borderLeftColor: defaultBorderColor,
        borderRightColor: defaultBorderColor,
        borderTopRightRadius: defaultBorderRadius,
        borderTopLeftRadius: borderTopLeftWithoutRadius ? 0 : defaultBorderRadius,
        borderBottomLeftRadius: defaultBorderRadius,
        borderBottomRightRadius: defaultBorderRadius,
    }

    if (parentIsSelected) {
        result = {
            ...result,
            borderTopColor: 'transparent',
            borderBottomColor: isLast ? colors.primaryDark : 'transparent',
            borderBottomLeftRadius: !isLast ? 0 : defaultBorderRadius,
            borderBottomRightRadius: !isLast ? 0 : defaultBorderRadius,
            borderTopRightRadius: 0,
        };
    }

    if (hasChildren && isActive && isOpen) {
        result = {
            ...result,
            borderBottomColor: 'transparent',
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
        };
    }

    if (isActive) {
        result.paddingTop = 0;
    }

    return result;
}

export enum LinesTableColumn {
    DESIGNATION = 'DESIGNATION',
    ELEMENT_IMAGE = 'ELEMENT_IMAGE',
    QUANTITY = 'QUANTITY',
    UNIT = 'UNIT',
    GROSS_UNIT_PRICE = 'GROSS_UNIT_PRICE',
    UNIT_PRICE = 'UNIT_PRICE',
    NET_PROFIT = 'NET_PROFIT',
    NET_PROFIT_PERCENTAGE = 'NET_PROFIT_PERCENTAGE',
    TOTAL_EXCLUDING_TAX = 'TOTAL_EXCLUDING_TAX',
    TOTAL_INCLUDING_TAX = 'TOTAL_INCLUDING_TAX',
    TAX = 'TAX',
    MARGIN = 'MARGIN',
    DISCOUNT = 'DISCOUNT',
    DECENNIAL_INSURANCE = 'DECENNIAL_INSURANCE',
    AGGREGATED_ELEMENTS = 'AGGREGATED_ELEMENTS',
    TOTAL_COST_UNIT_PRICE = 'TOTAL_COST_UNIT_PRICE',
    LINE_NUMBER = 'LINE_NUMBER',
}

export function isColumnVisible(hiddenColumns: LinesTableColumn[] | null, field: LinesTableColumn) {
    return !(hiddenColumns ?? []).find(c => c === field)
}

/**
 * Reorderable columns in the order shown by the ColumnsOptionsSidebar. DESIGNATION is intentionally
 * excluded — it is rendered as a fixed first column. ELEMENT_IMAGE and LINE_NUMBER are included
 * here so the indices stay in sync with the sidebar's drag positions, but the table cell renderers
 * skip them (they render nested inside DESIGNATION).
 */
export const REORDERABLE_LINE_COLUMNS: LinesTableColumn[] = [
    LinesTableColumn.ELEMENT_IMAGE,
    LinesTableColumn.LINE_NUMBER,
    LinesTableColumn.QUANTITY,
    LinesTableColumn.UNIT,
    LinesTableColumn.GROSS_UNIT_PRICE,
    LinesTableColumn.DISCOUNT,
    LinesTableColumn.AGGREGATED_ELEMENTS,
    LinesTableColumn.TOTAL_COST_UNIT_PRICE,
    LinesTableColumn.MARGIN,
    LinesTableColumn.DECENNIAL_INSURANCE,
    LinesTableColumn.UNIT_PRICE,
    LinesTableColumn.TOTAL_EXCLUDING_TAX,
    LinesTableColumn.TAX,
    LinesTableColumn.TOTAL_INCLUDING_TAX,
    LinesTableColumn.NET_PROFIT,
    LinesTableColumn.NET_PROFIT_PERCENTAGE,
];

export function getOrderedLineColumns(
    columnsOrder: { i: number; field: LinesTableColumn }[] | null | undefined
): LinesTableColumn[] {
    const cols = [...REORDERABLE_LINE_COLUMNS];
    for (const co of columnsOrder ?? []) {
        const sourceIndex = cols.indexOf(co.field);
        if (sourceIndex > -1) {
            const [removed] = cols.splice(sourceIndex, 1);
            const targetIndex = Math.max(0, Math.min(co.i, cols.length));
            cols.splice(targetIndex, 0, removed);
        }
    }
    return cols;
}

export function getLevelFromParentKeys(parentKey: string, parentKeys: string[]): number {
    const index = parentKeys.indexOf(parentKey);
    if (index === -1) {
        throw new Error(`Key "${parentKey}" not found in the parentKeys.`);
    }
    return parentKeys.length - index;
}

export function isLastInLevel(allLines: BudgetLineDto[], parentKey: string, lineKey: string): boolean {
    if (allLines.length === 0) {
        return false;
    }

    function isLast(lines: BudgetLineDto[]): boolean {
        const lastLine = lines[lines.length - 1];
        if (!lastLine) {
            return false;
        }

        const lastLineChildren = allLines.filter(x => x.parentKey === lastLine.key)

        if ((lastLine.lineType !== BudgetLineType.SECTION || (lastLine.lineType === BudgetLineType.SECTION && lastLine.isOpen)) && lastLineChildren && lastLineChildren.length > 0) {
            return isLast(lastLineChildren);
        }

        if (lastLine.key === lineKey) {
            return true;
        }

        return false;
    }

    const parentChildren = allLines.filter(x => x.parentKey === parentKey)
    return isLast(parentChildren);
}

/**
 * Resolves the single "deepest last descendant" line key of a given parent, cached
 * on the `(lines, parentKey)` reference so that BudgetLine instances can cheaply
 * compare against it without each running an O(n) tree walk per dispatch.
 */
let _lastInLevelCache: { lines: BudgetLineDto[]; parentKey: string; result: string | null } | null = null;
export function getLastInLevelKey(allLines: BudgetLineDto[], parentKey: string): string | null {
    if (_lastInLevelCache && _lastInLevelCache.lines === allLines && _lastInLevelCache.parentKey === parentKey) {
        return _lastInLevelCache.result;
    }
    if (allLines.length === 0) {
        _lastInLevelCache = { lines: allLines, parentKey, result: null };
        return null;
    }
    // Walk down the last-open-child chain from parentKey
    const childrenByParent = new Map<string, BudgetLineDto[]>();
    for (const l of allLines) {
        if (l.parentKey == null) continue;
        const arr = childrenByParent.get(l.parentKey);
        if (arr) arr.push(l);
        else childrenByParent.set(l.parentKey, [l]);
    }
    let currentParent = parentKey;
    let result: string | null = null;
    // Guard against pathological cycles
    for (let i = 0; i < allLines.length; i++) {
        const kids = childrenByParent.get(currentParent);
        if (!kids || kids.length === 0) break;
        const last = kids[kids.length - 1];
        const hasOpenChildren = (last.lineType !== BudgetLineType.SECTION || last.isOpen) && (childrenByParent.get(last.key)?.length ?? 0) > 0;
        if (hasOpenChildren) {
            currentParent = last.key;
            continue;
        }
        result = last.key;
        break;
    }
    _lastInLevelCache = { lines: allLines, parentKey, result };
    return result;
}

/**
 * Cached count of SECTION-typed lines across the whole budget. Used to avoid
 * O(n) filter per BudgetLine instance per dispatch.
 */
let _sectionsCountCache: { lines: BudgetLineDto[]; count: number } | null = null;
export function getTotalSectionLines(allLines: BudgetLineDto[]): number {
    if (_sectionsCountCache && _sectionsCountCache.lines === allLines) {
        return _sectionsCountCache.count;
    }
    let count = 0;
    for (const l of allLines) {
        if (l.lineType === BudgetLineType.SECTION) count++;
    }
    _sectionsCountCache = { lines: allLines, count };
    return count;
}

export function isLineBeingCut(allLines: BudgetLineDto[], lineKey: string, lineBeingCutKeys: string[]): boolean {
    if (allLines.length === 0 || lineBeingCutKeys.length === 0) {
        return false;
    }

    const cutSet = new Set(lineBeingCutKeys);

    const isCutOrParentCut = (currentKey: string): boolean => {
        if (cutSet.has(currentKey)) {
            return true;
        }

        const line = findLineByKey(currentKey, allLines);
        if (line && line.parentKey) {
            return isCutOrParentCut(line.parentKey);
        }

        return false;
    };

    return isCutOrParentCut(lineKey);
}

const mergeText = (
    newText?: BudgetElementParameterTextDto,
    existingText?: BudgetElementParameterTextDto
): BudgetElementParameterTextDto | undefined => {
    if (!newText) return existingText;
    if (!existingText) return newText;
    return { ...newText, text: existingText.text };
};

const mergeNumber = (
    newNumber?: BudgetElementParameterNumberDto,
    existingNumber?: BudgetElementParameterNumberDto
): BudgetElementParameterNumberDto | undefined => {
    if (!newNumber) return existingNumber;
    if (!existingNumber) return newNumber;
    return { ...newNumber, number: existingNumber.number };
};

const mergeQuantity = (
    newQuantity?: BudgetElementParameterQuantityDto,
    existingQuantity?: BudgetElementParameterQuantityDto
): BudgetElementParameterQuantityDto | undefined => {
    if (!newQuantity) return existingQuantity;
    if (!existingQuantity) return newQuantity;
    return { ...newQuantity, quantity: existingQuantity.quantity };
};

const mergePrice = (
    newPrice?: BudgetElementParameterPriceDto,
    existingPrice?: BudgetElementParameterPriceDto
): BudgetElementParameterPriceDto | undefined => {
    if (!newPrice) return existingPrice;
    if (!existingPrice) return newPrice;
    return { ...newPrice, price: existingPrice.price };
};

const mergeMinMax = (
    newMinMax?: BudgetLineParameterMinMaxDto,
    existingMinMax?: BudgetLineParameterMinMaxDto
): BudgetLineParameterMinMaxDto | undefined => {
    if (!newMinMax) return undefined;
    if (!existingMinMax) return newMinMax;
    return {
        ...newMinMax,
        id: existingMinMax.id,
        value: existingMinMax.value,
        valueExpression: existingMinMax.valueExpression,
        elements: mergeElements(newMinMax.elements, existingMinMax.elements)
    };
};

const mergeElements = (
    newElements?: BudgetLineParameterElementDto[],
    existingElements?: BudgetLineParameterElementDto[]
): BudgetLineParameterElementDto[] | undefined => {
    if (!newElements) return undefined;
    if (!existingElements || existingElements.length === 0) return newElements;

    const existingMap = new Map<string, BudgetLineParameterElementDto>();
    for (const el of existingElements) {
        existingMap.set(el.elementId, el);
    }

    return newElements.map(newEl => {
        const existingEl = existingMap.get(newEl.elementId);
        if (existingEl) {
            return {
                ...newEl,
                id: existingEl.id,
                key: existingEl.key,
                checked: existingEl.checked,
                quantity: existingEl.quantity,
                price: existingEl.price,
                checkedForParameters: existingEl.checkedForParameters,
                parameters: mergeWithExistingParameters(newEl.parameters || [], existingEl.parameters || [])
            };
        }
        return newEl;
    });
};

export const mergeWithExistingParameters = (newParameters: BudgetLineParameterDto[], existingParameters: BudgetLineParameterDto[]): BudgetLineParameterDto[] => {
    if (!existingParameters || existingParameters.length === 0) {
        return newParameters;
    }

    const existingByElementParamId = new Map<string, BudgetLineParameterDto>();
    const existingByFamilyParamId = new Map<string, BudgetLineParameterDto>();
    for (const param of existingParameters) {
        if (param.elementParameterId) {
            existingByElementParamId.set(param.elementParameterId, param);
        } else if (param.familyParameterId) {
            existingByFamilyParamId.set(param.familyParameterId, param);
        }
    }

    const mergedParameters = newParameters.map(newParam => {
        const existingParam = newParam.elementParameterId
            ? existingByElementParamId.get(newParam.elementParameterId)
            : (newParam.familyParameterId ? existingByFamilyParamId.get(newParam.familyParameterId) : null) ?? null;
        if (existingParam) {
            return {
                ...newParam,
                id: existingParam.id,
                budgetLineParameterElementId: existingParam.budgetLineParameterElementId,
                key: existingParam.key,
                supplierId: existingParam.supplierId,
                supplierName: existingParam.supplierName,
                date: existingParam.date,
                images: existingParam.images,
                text: mergeText(newParam.text, existingParam.text),
                number: mergeNumber(newParam.number, existingParam.number),
                quantity: mergeQuantity(newParam.quantity, existingParam.quantity),
                price: mergePrice(newParam.price, existingParam.price),
                minMax: mergeMinMax(newParam.minMax, existingParam.minMax),
                elements: mergeElements(newParam.elements, existingParam.elements),
                children: mergeWithExistingParameters(newParam.children || [], existingParam.children || [])
            };
        }
        return {
            ...newParam,
            children: newParam.children ? mergeWithExistingParameters(newParam.children, []) : []
        };
    });

    mergedParameters.sort((a, b) => a.position - b.position);

    return mergedParameters;
}

export const addNewParameters = (newParameters: BudgetLineParameterDto[], existingParameters: BudgetLineParameterDto[]): BudgetLineParameterDto[] => {
    if (!existingParameters || existingParameters.length === 0) {
        return newParameters;
    }
    if (!newParameters || newParameters.length === 0) {
        return existingParameters;
    }

    const existingByElementParamId = new Set<string>();
    const existingByFamilyParamId = new Set<string>();
    for (const param of existingParameters) {
        if (param.elementParameterId) {
            existingByElementParamId.add(param.elementParameterId);
        }
        if (param.familyParameterId) {
            existingByFamilyParamId.add(param.familyParameterId);
        }
    }

    const result = existingParameters.map(existingParam => ({
        ...existingParam,
        children: existingParam.children?.length
            ? addNewParameters(
                newParameters.find(np =>
                    (np.elementParameterId && np.elementParameterId === existingParam.elementParameterId) ||
                    (np.familyParameterId && np.familyParameterId === existingParam.familyParameterId)
                )?.children || [],
                existingParam.children
            )
            : existingParam.children
    }));

    const newOnly = newParameters.filter(newParam => {
        const matchesElement = newParam.elementParameterId && existingByElementParamId.has(newParam.elementParameterId);
        const matchesFamily = newParam.familyParameterId && existingByFamilyParamId.has(newParam.familyParameterId);
        return !matchesElement && !matchesFamily;
    });

    return [...result, ...newOnly].sort((a, b) => a.position - b.position);
};

export const getExpressionParameters = (currentLineKey: string): ExpressionParameter[] => {
    const t = i18n.t;
    const result: ExpressionParameter[] = [];
    const budget = store.getState().budget.budget;
    const lines = store.getState().budget.lines;

    const currentLine = findLineByKey(currentLineKey, lines);
    const currentParentLineKey = currentLine && currentLine.parentKey ? currentLine.parentKey : null;

    function createForParameters(parameters: BudgetLineParameterDto[], idsPrefix: string[], namesPrefix: string[]) {
        for (const parameter of parameters) {
            if (parameter.type === ParameterSectionType.PARAMETER) {
                switch (parameter.parameterType) {
                    case ParameterSectionParameterType.PRODUCTS:
                    case ParameterSectionParameterType.IMAGES:
                    case ParameterSectionParameterType.TEXT:
                        break;

                    case ParameterSectionParameterType.NUMBER_MIN_MAX:
                        result.push({
                            id: ExpressionSystem.formatParameterId([...idsPrefix, parameter.familyParameterId ?? '', 'parameter']),
                            name: ExpressionSystem.formatParameterName([...namesPrefix, parameter.parameterName ?? '']),
                            value: parameter.minMax?.value ?? 0,
                        });
                        break;

                    case ParameterSectionParameterType.NUMBER:
                        result.push({
                            id: ExpressionSystem.formatParameterId([...idsPrefix, parameter.familyParameterId ?? '', 'parameter']),
                            name: ExpressionSystem.formatParameterName([...namesPrefix, parameter.parameterName ?? '']),
                            value: parameter.number?.number ?? 0,
                        });
                        break;

                    case ParameterSectionParameterType.QUANTITY:
                        result.push({
                            id: ExpressionSystem.formatParameterId([...idsPrefix, parameter.familyParameterId ?? '', 'parameter']),
                            name: ExpressionSystem.formatParameterName([...namesPrefix, parameter.parameterName ?? '']),
                            value: parameter.quantity?.quantity ?? 0,
                        });
                        break;

                    case ParameterSectionParameterType.PRICE:
                        result.push({
                            id: ExpressionSystem.formatParameterId([...idsPrefix, parameter.familyParameterId ?? '', 'parameter']),
                            name: ExpressionSystem.formatParameterName([...namesPrefix, parameter.parameterName ?? '']),
                            value: parameter.price?.price ?? 0,
                        });
                        break;

                    case ParameterSectionParameterType.WEIGHT:
                        result.push({
                            id: ExpressionSystem.formatParameterId([...idsPrefix, parameter.familyParameterId ?? '', 'parameter']),
                            name: ExpressionSystem.formatParameterName([...namesPrefix, parameter.parameterName ?? '']),
                            value: parameter.number?.number ?? 0,
                        });
                        break;

                    default:
                        break;
                }

                // Also register catalog parameter ID so formulas from catalog resolve correctly
                if (parameter.catalogParameterId) {
                    const catalogParamValue = parameter.parameterType === ParameterSectionParameterType.NUMBER_MIN_MAX
                        ? (parameter.minMax?.value ?? 0)
                        : parameter.parameterType === ParameterSectionParameterType.QUANTITY
                            ? (parameter.quantity?.quantity ?? 0)
                            : parameter.parameterType === ParameterSectionParameterType.PRICE
                                ? (parameter.price?.price ?? 0)
                                : (parameter.number?.number ?? 0);

                    result.push({
                        id: ExpressionSystem.formatParameterId([...idsPrefix, parameter.catalogParameterId, 'parameter']),
                        name: ExpressionSystem.formatParameterName([...namesPrefix, parameter.parameterName ?? '']),
                        value: catalogParamValue,
                        hidden: true,
                    });
                }

                // Additionally register catalog-based parameter reference for parent line resolution
                if (idsPrefix[0] === 'CURRENT_PARENT_LINE') {
                    // Resolve catalogParameterId: use the field if available, otherwise match by name against catalog cache
                    let resolvedCatalogId = parameter.catalogParameterId;
                    if (!resolvedCatalogId && _catalogParamsCache && parameter.parameterName) {
                        const normalizedName = parameter.parameterName.trim().toLowerCase();
                        const matchingCatalog = _catalogParamsCache.find(cp => cp.name.trim().toLowerCase() === normalizedName);
                        if (matchingCatalog) {
                            resolvedCatalogId = matchingCatalog.id;
                        }
                    }

                    if (resolvedCatalogId) {
                        const paramValue = parameter.parameterType === ParameterSectionParameterType.NUMBER_MIN_MAX
                            ? (parameter.minMax?.value ?? 0)
                            : parameter.parameterType === ParameterSectionParameterType.QUANTITY
                                ? (parameter.quantity?.quantity ?? 0)
                                : parameter.parameterType === ParameterSectionParameterType.PRICE
                                    ? (parameter.price?.price ?? 0)
                                    : (parameter.number?.number ?? 0);

                        result.push({
                            id: ExpressionSystem.formatParameterId([...idsPrefix, 'CATALOG', resolvedCatalogId, 'parameter']),
                            name: ExpressionSystem.formatParameterName([...namesPrefix, parameter.parameterName ?? '']),
                            value: paramValue,
                            hidden: true,
                        });
                    }
                }
            } else {
                createForParameters(parameter.children ?? [], [...idsPrefix], [...namesPrefix, parameter.title ?? '']);
            }
        }
    }

    function createForBudget() {
        const budgetName = t('budgets.budget.budget');
        const budgetKey = 'budget';

        result.push({
            id: ExpressionSystem.formatParameterId([budgetKey, 'totalNetExcludingTax']),
            name: ExpressionSystem.formatParameterName([budgetName, t('budgets.budget.lines.totals.total_net_without_tax')]),
            value: budget.totalNetExcludingTax,
        });
        result.push({
            id: ExpressionSystem.formatParameterId([budgetKey, 'discount']),
            name: ExpressionSystem.formatParameterName([budgetName, t('budgets.budget.lines.totals.discount')]),
            value: budget.discount,
        });
        result.push({
            id: ExpressionSystem.formatParameterId([budgetKey, 'totalExcludingTax']),
            name: ExpressionSystem.formatParameterName([budgetName, t('budgets.budget.lines.totals.total_without_tax')]),
            value: budget.totalExcludingTax,
        });
        result.push({
            id: ExpressionSystem.formatParameterId([budgetKey, 'totalTax']),
            name: ExpressionSystem.formatParameterName([budgetName, t('budgets.budget.lines.totals.total_tax')]),
            value: budget.totalTax,
        });
        result.push({
            id: ExpressionSystem.formatParameterId([budgetKey, 'totalIncludingTax']),
            name: ExpressionSystem.formatParameterName([budgetName, t('budgets.budget.lines.totals.total_with_tax')]),
            value: budget.totalIncludingTax,
        });
        result.push({
            id: ExpressionSystem.formatParameterId([budgetKey, 'defaultDiscountPercentage']),
            name: ExpressionSystem.formatParameterName([budgetName, t('budgets.budget.default_remise')]),
            value: budget.defaultDiscountPercentage ?? 0,
        });
        result.push({
            id: ExpressionSystem.formatParameterId([budgetKey, 'defaultMarginPercentage']),
            name: ExpressionSystem.formatParameterName([budgetName, t('budgets.budget.default_margin')]),
            value: budget.defaultMarginPercentage ?? 0,
        });
        result.push({
            id: ExpressionSystem.formatParameterId([budgetKey, 'defaultCommissionMarginPercentage']),
            name: ExpressionSystem.formatParameterName([budgetName, t('budgets.budget.default_commission_margin')]),
            value: budget.defaultCommissionMarginPercentage ?? 0,
        });
        result.push({
            id: ExpressionSystem.formatParameterId([budgetKey, 'defaultCombinedMarginPercentage']),
            name: ExpressionSystem.formatParameterName([budgetName, t('budgets.budget.default_combined_margin')]),
            value: (budget.defaultMarginPercentage ?? 0) + (budget.defaultCommissionMarginPercentage ?? 0),
        });
        result.push({
            id: ExpressionSystem.formatParameterId([budgetKey, 'defaultDecennialInsurancePercentage']),
            name: ExpressionSystem.formatParameterName([budgetName, t('budgets.budget.default_decennial_insurance')]),
            value: budget.defaultDecennialInsurancePercentage ?? 0,
        });
        result.push({
            id: ExpressionSystem.formatParameterId([budgetKey, 'defaultTaxValue']),
            name: ExpressionSystem.formatParameterName([budgetName, t('budgets.budget.default_tax')]),
            value: budget.defaultTaxValue ?? 0,
        });
    }

    function createForLine(line: BudgetLineDto, idsPrefix: string[], namesPrefix: string[]) {
        result.push({
            id: ExpressionSystem.formatParameterId([...idsPrefix, 'quantity']),
            name: ExpressionSystem.formatParameterName([...namesPrefix, t('budgets.budget.lines.header.quantity')]),
            value: line.quantity,
        });
        result.push({
            id: ExpressionSystem.formatParameterId([...idsPrefix, 'grossUnitPrice']),
            name: ExpressionSystem.formatParameterName([...namesPrefix, t('budgets.budget.lines.header.gross_unit_price')]),
            value: line.grossUnitPrice,
        });
        result.push({
            id: ExpressionSystem.formatParameterId([...idsPrefix, 'totalCostUnitPrice']),
            name: ExpressionSystem.formatParameterName([...namesPrefix, t('budgets.budget.lines.header.total_cost_unit_price')]),
            value: MathUtils.plus(line.grossUnitPrice, MathUtils.multiply(line.grossUnitPrice, MathUtils.divide(line.discountPercentage, 100)) * -1, line.unitPriceWithChildren),
        });
        result.push({
            id: ExpressionSystem.formatParameterId([...idsPrefix, 'marginPercentage']),
            name: ExpressionSystem.formatParameterName([...namesPrefix, t('budgets.budget.lines.header.margin')]),
            value: line.marginPercentage,
        });
        result.push({
            id: ExpressionSystem.formatParameterId([...idsPrefix, 'discountPercentage']),
            name: ExpressionSystem.formatParameterName([...namesPrefix, t('budgets.budget.lines.header.discount')]),
            value: line.discountPercentage,
        });
        result.push({
            id: ExpressionSystem.formatParameterId([...idsPrefix, 'decennialInsurancePercentage']),
            name: ExpressionSystem.formatParameterName([...namesPrefix, t('budgets.budget.lines.header.decennial_insurance')]),
            value: line.decennialInsurancePercentage,
        });
        result.push({
            id: ExpressionSystem.formatParameterId([...idsPrefix, 'unitPrice']),
            name: ExpressionSystem.formatParameterName([...namesPrefix, t('budgets.budget.lines.header.price_unity')]),
            value: line.unitPrice,
        });
        result.push({
            id: ExpressionSystem.formatParameterId([...idsPrefix, 'totalUnitExcludingTax']),
            name: ExpressionSystem.formatParameterName([...namesPrefix, t('budgets.budget.lines.header.total_unit_without_tax')]),
            value: line.totalUnitExcludingTax,
        });
        result.push({
            id: ExpressionSystem.formatParameterId([...idsPrefix, 'unitPriceWithChildren']),
            name: ExpressionSystem.formatParameterName([...namesPrefix, t('budgets.budget.lines.header.aggregated_elements')]),
            value: line.unitPriceWithChildren,
        });
        const totalCostUnitPriceCalc = MathUtils.plus(line.grossUnitPrice, MathUtils.multiply(line.grossUnitPrice, MathUtils.divide(line.discountPercentage, 100)) * -1, line.unitPriceWithChildren);
        const netProfitValue = MathUtils.multiply(MathUtils.minus(line.unitPrice, totalCostUnitPriceCalc), line.quantity ?? 0);
        result.push({
            id: ExpressionSystem.formatParameterId([...idsPrefix, 'netProfit']),
            name: ExpressionSystem.formatParameterName([...namesPrefix, t('budgets.budget.lines.header.net_profit')]),
            value: netProfitValue,
        });
        result.push({
            id: ExpressionSystem.formatParameterId([...idsPrefix, 'netProfitPercentage']),
            name: ExpressionSystem.formatParameterName([...namesPrefix, t('budgets.budget.lines.header.net_profit_percentage')]),
            value: line.totalExcludingTax !== 0 ? MathUtils.multiply(MathUtils.divide(netProfitValue, line.totalExcludingTax), 100) : 0,
        });
        result.push({
            id: ExpressionSystem.formatParameterId([...idsPrefix, 'totalExcludingTax']),
            name: ExpressionSystem.formatParameterName([...namesPrefix, t('budgets.budget.lines.header.total_without_tax')]),
            value: line.totalExcludingTax,
        });
        result.push({
            id: ExpressionSystem.formatParameterId([...idsPrefix, 'totalIncludingTax']),
            name: ExpressionSystem.formatParameterName([...namesPrefix, t('budgets.budget.lines.header.total_with_tax')]),
            value: line.totalIncludingTax,
        });
        result.push({
            id: ExpressionSystem.formatParameterId([...idsPrefix, 'unitSymbol']),
            name: ExpressionSystem.formatParameterName([...namesPrefix, t('budgets.budget.lines.header.unity_tooltip')]),
            value: line.unitSymbol ?? '',
        });

        if (line.parameters && line.parameters.length > 0) {
            createForParameters(line.parameters, [...idsPrefix], [...namesPrefix]);
        }

        // Add composition (PRODUCTS) section totals from child SECTION lines
        try {
            const allChildSections = lines.filter((l: BudgetLineDto) => l.parentKey === line.key && l.lineType === BudgetLineType.SECTION);
            for (const section of allChildSections) {
                let strippedId: string | null = null;
                if (section.sourceFamilyParameterId) {
                    strippedId = section.sourceFamilyParameterId.replace(/[^0-9A-Za-z_]/g, '');
                } else if (line.parameters && line.parameters.length > 0) {
                    const findProductsParam = (params: BudgetLineParameterDto[]): BudgetLineParameterDto | undefined => {
                        for (const p of params) {
                            if (p.parameterType === ParameterSectionParameterType.PRODUCTS && p.parameterName === section.designation && p.familyParameterId) {
                                return p;
                            }
                            if (p.children && p.children.length > 0) {
                                const found = findProductsParam(p.children);
                                if (found) return found;
                            }
                        }
                        return undefined;
                    };
                    const matchedParam = findProductsParam(line.parameters);
                    if (matchedParam?.familyParameterId) {
                        strippedId = matchedParam.familyParameterId.replace(/[^0-9A-Za-z_]/g, '');
                    }
                }
                if (!strippedId) continue;

                const compositionFields: { field: string; value: number }[] = [
                    { field: 'totalExcludingTax', value: section.totalExcludingTax ?? 0 },
                    { field: 'totalIncludingTax', value: section.totalIncludingTax ?? 0 },
                ];
                for (const { field, value } of compositionFields) {
                    const displayName = ExpressionSystem.formatParameterName([...namesPrefix, section.designation ?? '', t(`budgets.budget.lines.header.${field === 'totalExcludingTax' ? 'total_without_tax' : 'total_with_tax'}`)]);
                    result.push({
                        id: ExpressionSystem.formatParameterId([...idsPrefix, strippedId, 'composition', field]),
                        name: displayName,
                        value,
                    });
                    if (idsPrefix[0] === 'CURRENT_LINE') {
                        result.push({
                            id: ExpressionSystem.formatParameterId([strippedId, 'composition', field]),
                            name: displayName,
                            value,
                            hidden: true,
                        });
                    }
                }
            }
        } catch (e) {
            console.error('Error building composition expression params (getExpressionParameters):', e);
        }
    }

    createForBudget();

    // Current line first
    if (currentLine) {
        createForLine(currentLine, ['CURRENT_LINE'], [`${t('budgets.budget.lines.current_line')}`]);
    }

    // Current parent line second — walk up hierarchy to find nearest element/component ancestor
    if (currentParentLineKey) {
        let parentLine = findLineByKey(currentParentLineKey, lines);
        while (parentLine && (parentLine.lineType === BudgetLineType.SECTION || parentLine.lineType === BudgetLineType.PAGE_BREAK || parentLine.lineType === BudgetLineType.NOTE)) {
            parentLine = parentLine.parentKey ? findLineByKey(parentLine.parentKey, lines) : null;
        }
        if (parentLine) {
            createForLine(parentLine, ['CURRENT_PARENT_LINE'], [`${t('budgets.budget.lines.current_parent_line')}`]);
        }
    }

    // Remaining lines in original order
    for (const line of lines) {
        if (line.key === currentLineKey || (currentParentLineKey && line.key === currentParentLineKey)) {
            continue;
        }

        const lineName = `${line.designation ? line.designation : (`(${t('budgets.budget.lines.without_designation')})`)} (${line.number})`;
        createForLine(line, [line.key], [lineName]);
    }

    // Add ANCESTOR_LINE parameters (hidden from dropdown, used for evaluation and display).
    if (currentLine) {
        const budgetLabel = t('budgets.budget.budget');
        const sectionLabel = t('budgets.budget.lines.add_options.section');
        const fieldTranslationKeys: Record<string, string> = {
            quantity: 'budgets.budget.lines.header.quantity_tooltip',
            grossUnitPrice: 'budgets.budget.lines.header.gross_unit_price',
            marginPercentage: 'budgets.budget.lines.header.margin',
            discountPercentage: 'budgets.budget.lines.header.discount',
            decennialInsurancePercentage: 'budgets.budget.lines.header.decennial_insurance',
            unitPrice: 'budgets.budget.lines.header.price_per_unity_tooltip',
            totalUnitExcludingTax: 'budgets.budget.lines.header.total_unit_without_tax_tooltip',
            unitPriceWithChildren: 'budgets.budget.lines.header.unit_price_with_children',
            totalExcludingTax: 'budgets.budget.lines.header.total_without_tax',
            totalIncludingTax: 'budgets.budget.lines.header.total_with_tax',
            unitSymbol: 'budgets.budget.lines.header.unity_tooltip',
        };
        for (const fieldKey of ANCESTOR_FIELD_KEYS) {
            const value = resolveAncestorFieldValue(currentLine, lines, fieldKey);
            result.push({
                id: ExpressionSystem.formatParameterId(['ANCESTOR_LINE', fieldKey]),
                name: ExpressionSystem.formatParameterName([budgetLabel, sectionLabel, t(fieldTranslationKeys[fieldKey] ?? fieldKey)]),
                value: value,
                hidden: false,
            });
        }
    }

    // Add catalog parameter references as fallback for display (ensures CURRENT_PARENT_LINE__CATALOG__ tokens resolve)
    if (_catalogParamsCache) {
        const parentLineLabel = t('catalog_parameters.parent_line_label');
        const budgetLbl = t('budgets.budget.budget');
        const numericTypes = ['NUMBER', 'QUANTITY', 'PRICE', 'NUMBER_MIN_MAX', 'WEIGHT'];
        const existingIds = new Set(result.map(r => r.id));
        for (const cp of _catalogParamsCache) {
            if (cp.parameterType && numericTypes.includes(cp.parameterType)) {
                const catalogId = ExpressionSystem.formatParameterId(['CURRENT_PARENT_LINE', 'CATALOG', cp.id, 'parameter']);
                if (!existingIds.has(catalogId)) {
                    result.push({
                        id: catalogId,
                        name: ExpressionSystem.formatParameterName([budgetLbl, parentLineLabel, cp.name]),
                        value: 0,
                    });
                }
            }
        }
    }

    return result;
}

export const ANCESTOR_FIELD_KEYS = [
    'quantity', 'grossUnitPrice', 'marginPercentage', 'discountPercentage',
    'decennialInsurancePercentage',
    'unitPrice', 'totalUnitExcludingTax', 'unitPriceWithChildren',
    'totalExcludingTax', 'totalIncludingTax', 'unitSymbol',
];

function getLineFieldValue(line: BudgetLineDto, fieldKey: string): number | string | null {
    switch (fieldKey) {
        case 'quantity': return line.quantity;
        case 'grossUnitPrice': return line.grossUnitPrice;
        case 'marginPercentage': return line.marginPercentage;
        case 'discountPercentage': return line.discountPercentage;
        case 'decennialInsurancePercentage': return line.decennialInsurancePercentage;
        case 'unitPrice': return line.unitPrice;
        case 'totalUnitExcludingTax': return line.totalUnitExcludingTax;
        case 'unitPriceWithChildren': return line.unitPriceWithChildren;
        case 'totalExcludingTax': return line.totalExcludingTax;
        case 'totalIncludingTax': return line.totalIncludingTax;
        case 'unitSymbol': return line.unitSymbol ?? '';
        default: return 0;
    }
}

export function resolveAncestorFieldValue(
    currentLine: BudgetLineDto,
    allLines: BudgetLineDto[],
    fieldKey: string
): number | string {
    // Collect all ancestors from current line to root (bottom-up)
    const ancestors: BudgetLineDto[] = [];
    let parentKey = currentLine.parentKey;
    while (parentKey) {
        const parentLine = findLineByKey(parentKey, allLines);
        if (!parentLine) break;
        ancestors.push(parentLine);
        parentKey = parentLine.parentKey;
    }

    // Reverse to get top-down order (root first), then find the first with a filled value
    ancestors.reverse();
    for (const ancestor of ancestors) {
        const value = getLineFieldValue(ancestor, fieldKey);
        if (fieldKey === 'unitSymbol') {
            if (value && value !== '') return value;
        } else {
            if (value !== 0 && value !== null && value !== undefined) return value;
        }
    }

    return fieldKey === 'unitSymbol' ? '' : 0;
}

/**
 * Converts element-style expression references (e.g., M_<familyParameterId>_E) to budget-style references
 * (e.g., M_CURRENT_LINE__familyParameterId__parameter_E).
 * This is called when an element is added to a budget line.
 */
function convertElementExpressionToBudgetFormat(
    expression: string | null | undefined,
    allParameters: BudgetLineParameterDto[]
): string | null {
    if (!expression) return null;

    // Collect all familyParameterIds from the parameters
    const familyParameterIds: string[] = [];

    function collectParameters(parameters: BudgetLineParameterDto[]) {
        for (const param of parameters) {
            if (param.familyParameterId) {
                familyParameterIds.push(param.familyParameterId);
            }
            if (param.children) {
                collectParameters(param.children);
            }
            if (param.elements) {
                for (const element of param.elements) {
                    if (element.parameters) {
                        collectParameters(element.parameters);
                    }
                }
            }
            if (param.minMax?.elements) {
                for (const element of param.minMax.elements) {
                    if (element.parameters) {
                        collectParameters(element.parameters);
                    }
                }
            }
        }
    }
    collectParameters(allParameters);

    // Replace M_<familyParameterId>_E (element format) with M_CURRENT_LINE__<familyParameterId>__parameter_E (budget format)
    let result: string = expression;
    for (const familyParamId of familyParameterIds) {
        // Element format: just the familyParameterId
        const elementId = ExpressionSystem.formatParameterId([familyParamId]);
        const elementFormat = ExpressionSystem.serializeParameterMentionId(elementId);

        // Budget format: CURRENT_LINE__familyParameterId__parameter
        const budgetId = ExpressionSystem.formatParameterId(['CURRENT_LINE', familyParamId, 'parameter']);
        const budgetFormat = ExpressionSystem.serializeParameterMentionId(budgetId);

        result = result.split(elementFormat).join(budgetFormat);
    }

    // Convert composition (PRODUCTS) references using regex to catch ALL composition mentions
    // M_<id>__composition__<field>_E → M_CURRENT_LINE__<id>__composition__<field>_E
    // Uses negative lookahead to skip already-converted mentions (those with CURRENT_LINE__ prefix)
    result = result.replace(/M_((?!CURRENT_LINE__)[0-9A-Za-z_]+__composition__[0-9A-Za-z_]+)_E/g, 'M_CURRENT_LINE__$1_E');

    // Convert BUDGET_FIELD_ELEMENT__<fieldKey> → CURRENT_LINE__<fieldKey> (own element line value)
    // Convert BUDGET_FIELD_SECTION__<fieldKey> → ANCESTOR_LINE__<fieldKey> (top-down ancestor value)
    for (const fieldKey of ANCESTOR_FIELD_KEYS) {
        // Element variant → maps to existing CURRENT_LINE fields
        const elementFieldId = ExpressionSystem.formatParameterId(['BUDGET_FIELD_ELEMENT', fieldKey]);
        const elementFieldFormat = ExpressionSystem.serializeParameterMentionId(elementFieldId);
        const currentLineId = ExpressionSystem.formatParameterId(['CURRENT_LINE', fieldKey]);
        const currentLineFormat = ExpressionSystem.serializeParameterMentionId(currentLineId);
        result = result.split(elementFieldFormat).join(currentLineFormat);

        // Section variant → maps to ANCESTOR_LINE (top-down resolution)
        const sectionFieldId = ExpressionSystem.formatParameterId(['BUDGET_FIELD_SECTION', fieldKey]);
        const sectionFieldFormat = ExpressionSystem.serializeParameterMentionId(sectionFieldId);
        const ancestorId = ExpressionSystem.formatParameterId(['ANCESTOR_LINE', fieldKey]);
        const ancestorFormat = ExpressionSystem.serializeParameterMentionId(ancestorId);
        result = result.split(sectionFieldFormat).join(ancestorFormat);

        // Parent variant → maps to CURRENT_PARENT_LINE (nearest element/component ancestor)
        const parentFieldId = ExpressionSystem.formatParameterId(['BUDGET_FIELD_PARENT', fieldKey]);
        const parentFieldFormat = ExpressionSystem.serializeParameterMentionId(parentFieldId);
        const parentLineId = ExpressionSystem.formatParameterId(['CURRENT_PARENT_LINE', fieldKey]);
        const parentLineFormat = ExpressionSystem.serializeParameterMentionId(parentLineId);
        result = result.split(parentFieldFormat).join(parentLineFormat);
    }

    return result;
}

/**
 * Converts all expressions in a parameter from element format to budget format.
 */
export function convertParameterExpressions(
    parameter: BudgetLineParameterDto,
    allParameters: BudgetLineParameterDto[]
): BudgetLineParameterDto {
    const converted = { ...parameter };

    // Convert number expression
    if (converted.number?.numberExpression) {
        converted.number = {
            ...converted.number,
            numberExpression: convertElementExpressionToBudgetFormat(converted.number.numberExpression, allParameters)
        };
    }

    // Convert quantity expression
    if (converted.quantity?.quantityExpression) {
        converted.quantity = {
            ...converted.quantity,
            quantityExpression: convertElementExpressionToBudgetFormat(converted.quantity.quantityExpression, allParameters)
        };
    }

    // Convert price expression
    if (converted.price?.priceExpression) {
        converted.price = {
            ...converted.price,
            priceExpression: convertElementExpressionToBudgetFormat(converted.price.priceExpression, allParameters)
        };
    }

    // Convert minMax expression
    if (converted.minMax?.valueExpression) {
        converted.minMax = {
            ...converted.minMax,
            valueExpression: convertElementExpressionToBudgetFormat(converted.minMax.valueExpression, allParameters)
        };
    }

    // Convert children recursively
    if (converted.children) {
        converted.children = converted.children.map(child => convertParameterExpressions(child, allParameters));
    }

    return converted;
}

function parameterHasFormulasOrValues(p: BudgetLineParameterDto): boolean {
    if (p.number?.numberExpression || p.number?.number != null) return true;
    if (p.quantity?.quantityExpression || p.quantity?.quantity != null) return true;
    if (p.price?.priceExpression || p.price?.price != null) return true;
    if (p.minMax?.valueExpression) return true;
    if (p.text?.text) return true;
    if (p.children?.some(c => parameterHasFormulasOrValues(c))) return true;
    return false;
}

export function hasExistingFamilyLevelFormulas(existingParameters: BudgetLineParameterDto[], newParameters: BudgetLineParameterDto[]): boolean {
    return newParameters.some(p => {
        if (p.elementParameterId) return false;
        const existing = existingParameters.find(ep => ep.familyParameterId === p.familyParameterId);
        if (!existing) return false;
        return parameterHasFormulasOrValues(existing);
    });
}

export function mapNewParametersToExisting(parameters: BudgetLineParameterDto[], existingParameters: BudgetLineParameterDto[], keepExistingFormulas = false): BudgetLineParameterDto[] {
    // First, convert all element expressions to budget format
    const convertedParameters = parameters.map(p => convertParameterExpressions(p, parameters));

    return convertedParameters.map(p => {
        const existingParameter = existingParameters.find(ep => ep.familyParameterId === p.familyParameterId);
        if (existingParameter) {
            const isFamilyLevel = !p.elementParameterId;
            const keepThis = keepExistingFormulas && isFamilyLevel;

            const mergedNumber = keepThis
                ? (existingParameter.number ?? p.number)
                : (p.number ?? existingParameter.number);
            const mergedQuantity = keepThis
                ? (existingParameter.quantity ?? p.quantity)
                : (p.quantity ?? existingParameter.quantity);
            const mergedPrice = keepThis
                ? (existingParameter.price ?? p.price)
                : (p.price ?? existingParameter.price);
            const mergedText = keepThis
                ? (existingParameter.text ?? p.text)
                : (p.text ?? existingParameter.text);
            const mergedElements = keepThis
                ? (existingParameter.elements ?? p.elements)
                : p.elements;
            const mergedImages = keepThis
                ? (existingParameter.images ?? p.images)
                : p.images;

            return {
                ...existingParameter,
                ...p,
                id: existingParameter.id,
                key: Utils.newGuid(),
                number: mergedNumber,
                quantity: mergedQuantity,
                price: mergedPrice,
                text: mergedText,
                images: mergedImages,
                children: mapNewParametersToExisting(p.children ?? [], existingParameter.children ?? [], keepExistingFormulas),
                elements: keepThis
                    ? (mergedElements?.map(product => {
                        const existingElement = existingParameter.elements?.find(ep => ep.elementId === product.elementId);
                        return {
                            ...product,
                            id: existingElement?.id ?? product.id,
                            parameters: mapNewParametersToExisting(product.parameters ?? [], existingElement?.parameters ?? [], keepExistingFormulas),
                        };
                    }) ?? [])
                    : (p.elements?.map(product => {
                        const existingElement = existingParameter.elements?.find(ep => ep.elementId === product.elementId);
                        return {
                            ...product,
                            id: existingElement?.id ?? product.id,
                            parameters: mapNewParametersToExisting(product.parameters ?? [], existingElement?.parameters ?? [], keepExistingFormulas),
                        };
                    }) ?? []),
                minMax: p.minMax
                    ? {
                        ...p.minMax,
                        id: existingParameter.minMax?.id ?? p.minMax.id,
                        valueExpression: keepThis
                            ? (existingParameter.minMax?.valueExpression ?? p.minMax?.valueExpression ?? null)
                            : (p.minMax?.valueExpression ?? null),
                        elements: p.minMax.elements?.map(product => {
                            const existingElement = existingParameter.minMax?.elements?.find(ep => ep.elementId === product.elementId);
                            return {
                                ...product,
                                id: existingElement?.id ?? product.id,
                                parameters: mapNewParametersToExisting(product.parameters ?? [], existingElement?.parameters ?? [], keepExistingFormulas),
                            };
                        }) ?? [],
                    }
                    : keepThis ? existingParameter.minMax : p.minMax,
            };
        } else {
            return {
                ...p,
                id: undefined,
                key: Utils.newGuid(),
                children: mapNewParametersToExisting(p.children ?? [], [], keepExistingFormulas),
                elements: p.elements?.map(product => ({
                    ...product,
                    parameters: mapNewParametersToExisting(product.parameters ?? [], [], keepExistingFormulas),
                })) ?? [],
                minMax: p.minMax
                    ? {
                        ...p.minMax,
                        elements: p.minMax.elements?.map(product => ({
                            ...product,
                            parameters: mapNewParametersToExisting(product.parameters ?? [], [], keepExistingFormulas),
                        })) ?? [],
                    }
                    : p.minMax,
            };
        }
    });
}

/**
 * Collects a mapping of parameterName → stripped familyParameterId from a line's parameters.
 * Only includes actual parameters (not sections), traversing sections recursively.
 */
export function collectParameterIdByName(parameters: BudgetLineParameterDto[]): Map<string, string> {
    const result = new Map<string, string>();

    function traverse(params: BudgetLineParameterDto[]) {
        for (const param of params) {
            if (param.type === ParameterSectionType.PARAMETER && param.familyParameterId && param.parameterName) {
                const strippedId = param.familyParameterId.replace(/[^0-9A-Za-z_]/g, '');
                result.set(param.parameterName, strippedId);
            }
            if (param.children) {
                traverse(param.children);
            }
        }
    }

    traverse(parameters);
    return result;
}

/**
 * Rewrites CURRENT_PARENT_LINE parameter mentions in an expression string,
 * replacing old familyParameterIds with new ones based on the idMapping.
 */
export function rewriteExpression(expression: string | null | undefined, idMapping: Map<string, string>): string | null | undefined {
    if (!expression) return expression;

    let result = expression;
    idMapping.forEach((newStrippedId, oldStrippedId) => {
        const oldMention = `M_CURRENT_PARENT_LINE__${oldStrippedId}__parameter_E`;
        const newMention = `M_CURRENT_PARENT_LINE__${newStrippedId}__parameter_E`;
        result = result.split(oldMention).join(newMention);
    });
    return result;
}

/**
 * Rewrites all expressions in a parameter tree, replacing CURRENT_PARENT_LINE mentions.
 */
export function rewriteParametersExpressions(parameters: BudgetLineParameterDto[], idMapping: Map<string, string>): BudgetLineParameterDto[] {
    return parameters.map(param => {
        const updated = { ...param };

        if (updated.number?.numberExpression) {
            updated.number = { ...updated.number, numberExpression: rewriteExpression(updated.number.numberExpression, idMapping) as string | null };
        }
        if (updated.quantity?.quantityExpression) {
            updated.quantity = { ...updated.quantity, quantityExpression: rewriteExpression(updated.quantity.quantityExpression, idMapping) as string | null };
        }
        if (updated.price?.priceExpression) {
            updated.price = { ...updated.price, priceExpression: rewriteExpression(updated.price.priceExpression, idMapping) as string | null };
        }
        if (updated.minMax?.valueExpression) {
            updated.minMax = { ...updated.minMax, valueExpression: rewriteExpression(updated.minMax.valueExpression, idMapping) as string | null };
        }
        if (updated.children) {
            updated.children = rewriteParametersExpressions(updated.children, idMapping);
        }

        return updated;
    });
}

/**
 * When lines are moved between parents (drag-and-drop), formulas referencing
 * CURRENT_PARENT_LINE need their parameter IDs rewritten to match the new parent's parameters.
 * Parameters are matched by name to find the corresponding ID on the new parent.
 */
export function rewriteParentLineExpressions(oldLines: BudgetLineDto[], newLines: BudgetLineDto[]): BudgetLineDto[] {
    return newLines.map(newLine => {
        const oldLine = oldLines.find(l => l.key === newLine.key);
        if (!oldLine || oldLine.parentKey === newLine.parentKey) {
            return newLine;
        }

        const oldParent = oldLine.parentKey ? oldLines.find(l => l.key === oldLine.parentKey) : null;
        const newParent = newLine.parentKey ? newLines.find(l => l.key === newLine.parentKey) : null;

        if (!oldParent || !newParent) {
            return newLine;
        }

        // Build mapping: old stripped familyParameterId → new stripped familyParameterId (matched by parameterName)
        const oldParamMap = collectParameterIdByName(oldParent.parameters); // name → oldStrippedId
        const newParamMap = collectParameterIdByName(newParent.parameters); // name → newStrippedId

        const idMapping: Map<string, string> = new Map();
        oldParamMap.forEach((oldStrippedId, name) => {
            const newStrippedId = newParamMap.get(name);
            if (newStrippedId && oldStrippedId !== newStrippedId) {
                idMapping.set(oldStrippedId, newStrippedId);
            }
        });

        if (idMapping.size === 0) {
            return newLine;
        }

        // Rewrite line-level expressions
        const updatedLine: BudgetLineDto = {
            ...newLine,
            quantityExpression: rewriteExpression(newLine.quantityExpression, idMapping) as string | null,
            discountPercentageExpression: rewriteExpression(newLine.discountPercentageExpression, idMapping) as string | null,
            totalExcludingTaxExpression: rewriteExpression(newLine.totalExcludingTaxExpression, idMapping) as string | null,
            totalUnitExcludingTaxExpression: rewriteExpression(newLine.totalUnitExcludingTaxExpression, idMapping) as string | null,
            marginPercentageExpression: rewriteExpression(newLine.marginPercentageExpression, idMapping) as string | null,
            grossUnitPriceExpression: rewriteExpression(newLine.grossUnitPriceExpression, idMapping) as string | null,
            parameters: rewriteParametersExpressions(newLine.parameters, idMapping),
        };

        return updatedLine;
    });
}
